import type defaultLocale from '@readme/iso/src/i18n/locales/default';

import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useSuperHubStore } from '@core/store';

import Icon from '@ui/Icon';
import Tooltip from '@ui/Tooltip';

import './style.scss';
import { formatAbsoluteTime, formatRelativeTime } from './util';

function getTimeLocaleKeys(attributed: boolean, prefix?: 'posted' | 'updated') {
  let key: keyof (typeof defaultLocale)['time']['absolute' | 'justNow' | 'relative'];

  switch (prefix) {
    case 'posted':
      key = attributed ? 'postedPrefixAttributed' : 'postedPrefix';
      break;
    case 'updated':
      key = attributed ? 'updatedPrefixAttributed' : 'updatedPrefix';
      break;
    default:
      key = attributed ? 'noPrefixAttributed' : 'noPrefix';
  }

  return {
    justNow: key,
    relative: key,
  };
}

interface LocalizedDateLineProps extends React.HTMLAttributes<HTMLElement> {
  /**
   * Add a name of a person or entity to attribute the date line to.
   * Will append a "by [attribution]" suffix to the date line.
   */
  attribution?: string;
  /**
   * Apply exact date formatting instead of relative time.
   */
  exactDate?: boolean;
  /**
   * Icon to display alongside the date line.
   */
  icon?: 'clock' | 'watch';
  /**
   * Prefix label for the date line.
   */
  labelPrefix?: 'posted' | 'updated';
  /**
   * HTML tag to use for the date line container.
   */
  tag?: React.ElementType;
  /**
   * The time to display in the date line.
   */
  time: Date | string;
  /**
   * Whether to show a tooltip with the full date and time on hover.
   */
  tooltip?: boolean;
}

const LocalizedDateLine: React.FC<React.PropsWithChildren<LocalizedDateLineProps>> = React.memo(
  function LocalizedDateLine({
    attribution,
    exactDate = false,
    icon,
    labelPrefix,
    time,
    tag: Tag = 'div',
    tooltip = false,
    className,
    style,
  }: LocalizedDateLineProps) {
    const { t } = useTranslation();
    const language = useSuperHubStore(s => s.language);

    const localizedTime = useMemo(() => {
      const timeLocaleKeys = getTimeLocaleKeys(!!attribution, labelPrefix);

      if (exactDate) {
        const absoluteTime = formatAbsoluteTime(time, language);
        return t(`time.absolute.${timeLocaleKeys.relative}`, {
          time: absoluteTime,
          attribution,
        });
      }

      const relativeTime = formatRelativeTime(time, language);

      return relativeTime === 'just-now'
        ? t(`time.justNow.${timeLocaleKeys.justNow}`, { attribution })
        : t(`time.relative.${timeLocaleKeys.relative}`, { time: relativeTime, attribution });
    }, [attribution, exactDate, labelPrefix, language, t, time]);

    if (!time) return null;

    const tagElement = (
      <Tag
        className={`DateLine ${className || ''}`}
        data-testid="localized-date-line"
        style={style}
        suppressHydrationWarning
      >
        {!!icon && <Icon aria-hidden="true" className="DateLine-icon" name={icon} />}
        {localizedTime}
      </Tag>
    );

    if (tooltip) {
      return (
        <Tooltip content={formatAbsoluteTime(time, language, { dateStyle: 'medium', timeStyle: 'short' })}>
          {tagElement}
        </Tooltip>
      );
    }

    return tagElement;
  },
);

export default LocalizedDateLine;
